/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
// ============================================================================
// USE LAZY COMPONENT - Hook for lazy loading components with Suspense
// ============================================================================

import { createLazyComponent } from "@/lib/performance/lazy-loading";
import React, { Suspense, type ComponentType, type ReactNode } from "react";

export interface UseLazyComponentOptions {
  /**
   * Custom loading fallback component
   */
  loading?: ComponentType | (() => ReactNode);
  /**
   * Disable SSR for this component
   */
  ssr?: boolean;
  /**
   * Component name for debugging
   */
  name?: string;
}

export interface UseLazyComponentReturn<
  T extends ComponentType<Record<string, unknown>>,
> {
  /**
   * Lazy-loaded component wrapped with Suspense
   */
  Component: ComponentType<React.ComponentProps<T>>;
  /**
   * Suspense wrapper component (includes fallback)
   */
  SuspenseWrapper: ComponentType<{ children: ReactNode }>;
}

/**
 * Hook for lazy loading components with built-in Suspense boundary
 *
 * Provides a consistent pattern for lazy loading components with proper
 * error handling and loading states.
 *
 * @example
 * ```tsx
 * const { Component: LazyEstimateBuilder } = useLazyComponent(
 *   () => import("@/components/features/estimates/estimate-builder"),
 *   { name: "EstimateBuilder" }
 * );
 *
 * return <LazyEstimateBuilder estimateId={id} />;
 * ```
 *
 * @example With custom loading
 * ```tsx
 * const { Component: LazyChart } = useLazyComponent(
 *   () => import("@/components/charts/revenue-chart"),
 *   {
 *     loading: () => <ChartSkeleton />,
 *     name: "RevenueChart"
 *   }
 * );
 * ```
 */
export function useLazyComponent<
  T extends ComponentType<Record<string, unknown>>,
>(
  importFn: () => Promise<{ default: T }>,
  options: UseLazyComponentOptions = {},
): UseLazyComponentReturn<T> {
  const { loading, ssr = true, name } = options;

  const LazyComponent = createLazyComponent(importFn, {
    loading,
    ssr,
    name,
  });

  const SuspenseWrapper = ({ children }: { children: ReactNode }) => {
    let fallback: ReactNode = null;
    if (loading) {
      // Check if it's a function component (not a class component)
      if (
        typeof loading === "function" &&
        !("prototype" in loading && loading.prototype?.isReactComponent)
      ) {
        // Function component or render function
        fallback = (loading as () => ReactNode)();
      } else {
        // Class component or ComponentType
        fallback = React.createElement(loading as ComponentType, {});
      }
    }
    return React.createElement(Suspense, { fallback }, children);
  };

  SuspenseWrapper.displayName = name
    ? `${name}SuspenseWrapper`
    : "SuspenseWrapper";

  return {
    Component: LazyComponent,
    SuspenseWrapper,
  };
}
